import { useState, useCallback, useRef, useEffect } from 'react';
export function useBaseCostDialogForm({ open, mode, equipmentId, baseCost, fuelSurcharge, isSaving, onSave, onClose, }) {
    const [showErrors, setShowErrors] = useState(false);
    const initialEquipmentRef = useRef('');
    const initialBaseCostRef = useRef('');
    const initialFscRef = useRef('');
    // Capture initial values when dialog opens
    useEffect(() => {
        if (open) {
            initialEquipmentRef.current = equipmentId;
            initialBaseCostRef.current = baseCost.amount;
            initialFscRef.current = fuelSurcharge.amount;
        }
    }, [open]);
    const handleClose = useCallback(() => {
        setShowErrors(false);
        initialEquipmentRef.current = '';
        initialBaseCostRef.current = '';
        initialFscRef.current = '';
        onClose();
    }, [onClose]);
    const isAdd = mode === 'add';
    const baseCostNum = baseCost.amount ? parseFloat(baseCost.amount) : NaN;
    const fscNum = fuelSurcharge.amount ? parseFloat(fuelSurcharge.amount) : NaN;
    const hasBaseCostAmount = !isNaN(baseCostNum) && baseCostNum > 0;
    const hasFscAmount = !isNaN(fscNum) && fscNum > 0;
    const hasValidBaseCost = !isNaN(baseCostNum);
    const hasValidFsc = !isNaN(fscNum);
    // Detect changes from initial values (edit mode)
    const origBaseCostNum = initialBaseCostRef.current
        ? Number(initialBaseCostRef.current)
        : NaN;
    const baseCostAmountChanged = !isNaN(origBaseCostNum) &&
        (isNaN(baseCostNum) || baseCostNum !== origBaseCostNum);
    const origFscNum = initialFscRef.current
        ? Number(initialFscRef.current)
        : NaN;
    const fscAmountChanged = !isNaN(origFscNum) && (isNaN(fscNum) || fscNum !== origFscNum);
    const equipmentChanged = !isAdd &&
        Boolean(initialEquipmentRef.current) &&
        equipmentId !== initialEquipmentRef.current;
    // In edit mode, adjustment reason is required when amount or equipment changed
    const baseCostReasonRequired = !isAdd && (baseCostAmountChanged || equipmentChanged);
    const fscReasonRequired = !isAdd && (fscAmountChanged || equipmentChanged);
    const missingRequiredReason = (baseCostReasonRequired && !baseCost.adjustmentReason) ||
        (fscReasonRequired && !fuelSurcharge.adjustmentReason);
    const hasNegativeAmount = (!isNaN(baseCostNum) && baseCostNum < 0) || (!isNaN(fscNum) && fscNum < 0);
    // In add mode, base cost is required; FSC is optional
    const missingRequiredBaseCost = isAdd && !hasBaseCostAmount;
    const hasAnyValue = hasBaseCostAmount || hasFscAmount;
    const hasAnyChange = (baseCostAmountChanged && hasValidBaseCost) ||
        (fscAmountChanged && hasValidFsc) ||
        equipmentChanged;
    const isSaveDisabled = isSaving ||
        !equipmentId ||
        missingRequiredBaseCost ||
        (!isAdd && !hasAnyValue && !hasAnyChange) ||
        missingRequiredReason ||
        hasNegativeAmount;
    const equipmentError = showErrors && !equipmentId;
    const handleSave = useCallback(() => {
        if (isSaveDisabled) {
            setShowErrors(true);
            return;
        }
        onSave();
    }, [isSaveDisabled, onSave]);
    return {
        showErrors,
        equipmentError,
        baseCostReasonRequired,
        fscReasonRequired,
        handleSave,
        handleClose,
    };
}
